import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "./context/CartContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Products from "./pages/Products";
import About from "./pages/About";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import Contact from "./pages/Contact";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminLayout from "./components/AdminLayout";
import AdminProducts from "./pages/admin/AdminProducts";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import OrdersPage from "./pages/OrdersPage";
import ProfilePage from "./pages/ProfilePage";
import AdminVouchers from "./pages/admin/AdminVouchers";
import AdminWheel from "./pages/admin/AdminWheel";
import AdminPopularProducts from "./pages/admin/AdminPopularProducts";
import VoucherHistory from "./pages/VoucherHistory";
import LuckyWheel from "./components/LuckyWheel";
import CareGuide from "./pages/CareGuide";
import SizeGuide from "./pages/SizeGuide";
import PolicyPage from "./pages/PolicyPage";
import AdminPolicies from "./pages/admin/AdminPolicies";
import AdminFooter from "./pages/admin/AdminFooter";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminBrands from "./pages/admin/AdminBrands";
import AdminSupplies from "./pages/admin/AdminSupplies";
import AdminRevenue from "./pages/admin/AdminRevenue";
import AdminPayment from "./pages/admin/AdminPayment";
import AdminHomepage from "./pages/admin/AdminHomepage";
import AdminBanners from "./pages/admin/AdminBanners";
import ScrollToTop from "./components/ScrollToTop";
import WishlistPage from "./pages/WishlistPage";
import FloatingCart from "./components/FloatingCart";
import FloatingContact from "./components/FloatingContact";
import Footer from "./components/Footer";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";

const ConditionalFooter = () => {
  const location = useLocation();
  const hideOnPaths = ["/checkout", "/login", "/register", "/admin", "/order-success"];
  const shouldHide = hideOnPaths.some((path) =>
    location.pathname.startsWith(path),
  );

  if (shouldHide) return null;
  return <Footer />;
};

function App() {
  return (
    <CartProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/about" element={<About />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/articles/:slug" element={<ArticleDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/guide/care" element={<CareGuide />} />
          <Route path="/guide/size" element={<SizeGuide />} />

          <Route path="/contact" element={<Contact />} />
          <Route path="/policy/:slug" element={<PolicyPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route
            path="/order-success/:orderId"
            element={<OrderSuccessPage />}
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vouchers"
            element={
              <ProtectedRoute>
                <VoucherHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wishlist"
            element={
              <ProtectedRoute>
                <WishlistPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="popular" element={<AdminPopularProducts />} />
            <Route path="vouchers" element={<AdminVouchers />} />
            <Route path="wheel" element={<AdminWheel />} />
            <Route path="policies" element={<AdminPolicies />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="brands" element={<AdminBrands />} />
            <Route path="supplies" element={<AdminSupplies />} />
            <Route path="revenue" element={<AdminRevenue />} />
            <Route path="footer" element={<AdminFooter />} />
            <Route path="payment" element={<AdminPayment />} />
            <Route path="homepage" element={<AdminHomepage />} />
            <Route path="banners" element={<AdminBanners />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ConditionalFooter />
        <FloatingCart />
        <FloatingContact />
        <ToastContainer
          position="top-center"
          autoClose={1800}
          hideProgressBar
          closeOnClick
          pauseOnHover={false}
        />
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;
